import React from 'react'
import dayjs from 'dayjs'
import { startCase } from 'lodash-es'
import { useTranslation } from 'react-i18next'
import {
  DataTable,
  DataTableCellVariants,
  Highlight,
  NoData,
  type DataTableHeaderProps,
  type DataTableRowData,
} from '@node-space/ui-components'
import { DateFormat } from 'constants/General'
import { type EarnAssetTransaction } from '../types'
import {
  EARN_TRANSACTION_STATUS_LABEL_KEYS,
  EARN_TRANSACTION_TYPE_LABEL_KEYS,
  getEarnTransactionCategory,
  getEarnTransactionStatus,
  getEarnTransactionStatusHighlightType,
} from '../utils/earnTransactionMapping'
import { formatAmount } from '../utils/utils'

export interface EarnAssetHistoryTableProps {
  transactions: EarnAssetTransaction[]
}

const getTransactionTypeLabel = (transaction: EarnAssetTransaction, t: (key: string) => string) => {
  const category = getEarnTransactionCategory(transaction)
  return category ? t(EARN_TRANSACTION_TYPE_LABEL_KEYS[category]) : startCase(transaction.type)
}

export const EarnAssetHistoryTable = ({ transactions }: EarnAssetHistoryTableProps) => {
  const { t } = useTranslation()

  if (transactions.length === 0) {
    return <NoData dataName="transactions" />
  }

  const headers: DataTableHeaderProps[] = [
    { key: 'date', label: t('earn.historyDateColumn'), variant: DataTableCellVariants.TEXT },
    { key: 'type', label: t('earn.historyTypeColumn'), variant: DataTableCellVariants.TEXT },
    { key: 'amount', label: t('earn.historyAmountColumn'), variant: DataTableCellVariants.TEXT },
    {
      key: 'status',
      label: t('earn.historyStatusColumn'),
      variant: DataTableCellVariants.HIGHLIGHT,
    },
  ]

  const data: DataTableRowData[] = transactions.map(transaction => ({
    id: transaction.transactionId,
    cells: [
      {
        key: 'date',
        variant: DataTableCellVariants.TEXT,
        Text: dayjs(transaction.createdAt).format(DateFormat.DD_MMM_YYYY),
      },
      {
        key: 'type',
        variant: DataTableCellVariants.TEXT,
        Text: getTransactionTypeLabel(transaction, t),
      },
      {
        key: 'amount',
        variant: DataTableCellVariants.TEXT,
        Text: `${formatAmount(Number(transaction.amount?.value ?? 0))} ${transaction.amount?.currencyCode}`,
      },
      {
        key: 'status',
        variant: DataTableCellVariants.HIGHLIGHT,
        Highlights: [
          <Highlight
            key={transaction.transactionId}
            type={getEarnTransactionStatusHighlightType(transaction)}
          >
            {t(EARN_TRANSACTION_STATUS_LABEL_KEYS[getEarnTransactionStatus(transaction)])}
          </Highlight>,
        ],
      },
    ],
  }))

  return <DataTable headers={headers} data={data} />
}
